import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getTranslation } from 'helpers/getTranslation';

import { Loading } from 'components/shared/Loading';
import { SelectTabs, type SelectTabsItem } from 'components/shared/SelectTabs';
import { Switch } from 'components/shared/Switch';
import { Typography } from 'components/shared/Typography';

import { EmptyState } from 'components/EmptyState';

import { JourneySummary } from './components/JourneySummary';
import { SessionList } from './components/SessionList';
import { SessionDetail } from './components/SessionDetail';
import { ExitScreens } from './components/ExitScreens';
import { ExitRules } from './components/ExitRules';

import type {
  AnalyticsStudentSessionDetailDto,
  ExitCause,
  ExitScreenStat,
  JourneyHighlight,
  JourneySummaryStats,
  SessionDayGroup,
  SessionSummary,
} from './TimelineSection.helpers';
import { JourneyViewContext } from './journeyTechMode';

import styles from './TimelineSection.module.scss';

const TimelineSection = ({
  loading,
  loadError,
  onlyProblems,
  techMode,
  studentId,
  detailLoading,
  detailError,
  truncated,
  platform,
  studentEmail,
  totalSessions,
  visibleSessionsCount,
  platformSessionsCount,
  selectedSessionId,
  selectedSession,
  detail,
  highlight,
  summary,
  studentStory,
  platformTabs,
  sessionGroups,
  exitScreenStats,
  onPlatformChange,
  onOnlyProblemsChange,
  onTechModeChange,
  onSessionSelect,
  onCauseSessionShow,
  onCauseEventJump,
  onScreenJump,
  onDetailRetry,
}: {
  loading: boolean;
  loadError: boolean;
  onlyProblems: boolean;
  techMode: boolean;
  studentId: string | null;
  detailLoading: boolean;
  detailError: boolean;
  truncated: boolean;
  platform: string;
  studentEmail?: string | null;
  totalSessions: number;
  visibleSessionsCount: number;
  platformSessionsCount: number;
  selectedSessionId: string | null;
  selectedSession: SessionSummary | null;
  detail: AnalyticsStudentSessionDetailDto | null;
  highlight: JourneyHighlight | null;
  summary: JourneySummaryStats;
  studentStory: string[];
  platformTabs: SelectTabsItem[];
  sessionGroups: SessionDayGroup[];
  exitScreenStats: ExitScreenStat[];
  onPlatformChange: (value: string) => void;
  onOnlyProblemsChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onTechModeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSessionSelect: (sessionId: string) => void;
  onCauseSessionShow: (cause: ExitCause) => void;
  onCauseEventJump: (eventIndex: number) => void;
  onScreenJump: (screenIndex: number) => void;
  onDetailRetry: () => void;
}) => {
  useTranslation();
  const viewSettings = useMemo(
    () => ({ techMode, studentId }),
    [techMode, studentId],
  );

  if (loading) {
    return (
      <div className={styles.section}>
        <div className={styles.loading}>
          <Loading />
        </div>
      </div>
    );
  }

  if (loadError || totalSessions === 0) {
    return (
      <div className={styles.section}>
        <EmptyState
          className={styles.emptyState}
          icon={loadError ? 'warning' : 'history'}
          title={getTranslation(
            loadError
              ? 'student_page.journey.error_title'
              : 'student_page.journey.empty_title',
          )}
          text={getTranslation(
            loadError
              ? 'student_page.journey.error_text'
              : 'student_page.journey.empty_text',
          )}
        />
      </div>
    );
  }

  return (
    <JourneyViewContext.Provider value={viewSettings}>
      <div className={styles.section}>
        <div className={styles.toolbar}>
          <SelectTabs
            value={platform}
            items={platformTabs}
            onChange={onPlatformChange}
          />
          <div className={styles.toolbarFilters}>
            <label className={styles.problemsFilter}>
              <Switch
                size="sm"
                checked={onlyProblems}
                onChange={onOnlyProblemsChange}
              />
              <Typography
                className={styles.problemsFilterLabel}
                component="span"
                variant="body2"
                weight="medium"
                mode="compact"
              >
                {getTranslation('student_page.journey.only_problems')}
              </Typography>
            </label>
            <label
              className={styles.problemsFilter}
              title={getTranslation('student_page.journey.tech_details_hint')}
            >
              <Switch
                size="sm"
                checked={techMode}
                onChange={onTechModeChange}
              />
              <Typography
                className={styles.problemsFilterLabel}
                component="span"
                variant="body2"
                weight="medium"
                mode="compact"
              >
                {getTranslation('student_page.journey.tech_details')}
              </Typography>
            </label>
          </div>
        </div>

        {truncated ? (
          <Typography
            className={styles.truncatedHint}
            variant="body3"
            mode="compact"
          >
            {getTranslation('student_page.journey.truncated_hint')}
          </Typography>
        ) : null}

        <JourneySummary
          summary={summary}
          story={studentStory}
          onCauseSessionShow={onCauseSessionShow}
        />

        <div className={styles.panes}>
          <SessionList
            selectedSessionId={selectedSessionId}
            sessionsCount={visibleSessionsCount}
            sessionGroups={sessionGroups}
            onSessionSelect={onSessionSelect}
          />
          <SessionDetail
            loading={detailLoading}
            error={detailError}
            session={selectedSession}
            detail={detail}
            highlight={highlight}
            studentEmail={studentEmail}
            onCauseEventJump={onCauseEventJump}
            onScreenJump={onScreenJump}
            onRetry={onDetailRetry}
          />
        </div>

        <div className={styles.bottomRow}>
          <ExitScreens
            totalSessions={platformSessionsCount}
            exitScreenStats={exitScreenStats}
          />
          <ExitRules />
        </div>
      </div>
    </JourneyViewContext.Provider>
  );
};

export default React.memo(TimelineSection) as any;
